// The interaction vocabulary every chart shares: one tooltip design, one
// legend that toggles series, one view switch, and one hover readout for the
// HTML charts. Same look on every page and every chart.
import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { ConsoleGlyph } from "../icons";

/** Series colours by role: 1 the measure, 2 the comparison (target, competitor), 3 and 4 further series, 5 the rest. */
export const VIZ_COLORS = ["var(--viz-1)", "var(--viz-2)", "var(--viz-3)", "var(--viz-6)", "var(--color-ink-subtle)"] as const;

/** A colour for series index `i`, by role, cycling past the fifth. */
export function vizColor(i: number): string {
  return VIZ_COLORS[i % VIZ_COLORS.length]!;
}

export interface TipRow {
  label: string;
  value: string;
  color?: string;
}

/** The one tooltip: a title line, then a swatch, a name, and a value per row. Surface, one hairline, no shadow. */
export function VizTip({ title, rows }: { title?: string; rows: TipRow[] }) {
  return (
    <div className="viz-tip" role="presentation">
      {title ? <p className="viz-tip-title">{title}</p> : null}
      {rows.map((row, i) => (
        <p className="viz-tip-row" key={`${row.label}-${i}`}>
          {row.color ? <span className="viz-swatch" style={{ background: row.color }} aria-hidden="true" /> : null}
          <span className="viz-tip-label">{row.label}</span>
          <span className="viz-tip-value">{row.value}</span>
        </p>
      ))}
    </div>
  );
}

interface RechartsTipProps {
  active?: boolean;
  label?: unknown;
  payload?: ReadonlyArray<{ name?: unknown; value?: unknown; color?: string; dataKey?: unknown; stroke?: string; fill?: string }>;
  format: (n: number) => string;
  /** Series key to display name, when the payload names are keys. */
  names?: Record<string, string>;
  colors?: Record<string, string>;
}

/** recharts adapter for VizTip. */
export function RechartsTip({ active, label, payload, format, names, colors }: RechartsTipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload
    .filter((p) => typeof p.value === "number")
    .map((p) => {
      const key = String(p.dataKey ?? p.name ?? "");
      return {
        label: names?.[key] ?? String(p.name ?? key),
        value: format(p.value as number),
        color: colors?.[key] ?? p.color ?? p.stroke ?? p.fill,
      };
    });
  return <VizTip title={label === undefined || label === "" ? undefined : String(label)} rows={rows} />;
}

export interface LegendItem {
  key: string;
  label: string;
  color: string;
  /** Drawn as a line (a target), not a toggle. */
  fixed?: boolean;
}

export interface VizLegendProps {
  label: string;
  items: LegendItem[];
  hidden: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onFocus?: (key: string | null) => void;
}

/**
 * Series keys as toggle buttons (aria-pressed), 44px tall each. The last
 * visible series cannot be switched off. Hover or focus a key and the chart
 * dims every other series.
 */
export function VizLegend({ label, items, hidden, onToggle, onFocus }: VizLegendProps) {
  const visible = items.filter((item) => !item.fixed && !hidden.has(item.key)).length;
  return (
    <div className="viz-legend" role="group" aria-label={label}>
      {items.map((item) =>
        item.fixed ? (
          <span className="viz-legend-item" data-fixed="" key={item.key}>
            <span className="viz-swatch" data-shape="line" style={{ background: item.color }} aria-hidden="true" />
            <span>{item.label}</span>
          </span>
        ) : (
          <button
            type="button"
            className="viz-legend-item"
            key={item.key}
            aria-pressed={!hidden.has(item.key)}
            aria-disabled={!hidden.has(item.key) && visible <= 1 ? true : undefined}
            onClick={() => {
              if (!hidden.has(item.key) && visible <= 1) return;
              onToggle(item.key);
            }}
            onPointerEnter={() => onFocus?.(item.key)}
            onPointerLeave={() => onFocus?.(null)}
            onFocus={() => onFocus?.(item.key)}
            onBlur={() => onFocus?.(null)}
          >
            <span className="viz-swatch" style={{ background: item.color }} aria-hidden="true" />
            <span>{item.label}</span>
          </button>
        ),
      )}
    </div>
  );
}

/** Hidden series and the focused series for a chart with a toggling legend. */
export function useSeriesState() {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const toggle = useCallback((key: string) => {
    setHidden((was) => {
      const next = new Set(was);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  return { hidden, focus, toggle, setFocus };
}

export interface ViewOption<V extends string = string> {
  value: V;
  label: string;
}

export interface ViewSwitchProps<V extends string = string> {
  /** Accessible name, e.g. "Tampilan grafik". */
  label: string;
  options: ViewOption<V>[];
  value: V;
  onChange: (value: V) => void;
}

/** The longest option a segmented switch still carries; longer, or more than three options, becomes a select. */
export const SEGMENT_LABEL_MAX = 12;

/** A card's view switch: a segmented control of two or three short options, else a 44px select. */
export function ViewSwitch<V extends string = string>({ label, options, value, onChange }: ViewSwitchProps<V>) {
  const segmented = options.length <= 3 && options.every((o) => o.label.length <= SEGMENT_LABEL_MAX);
  if (segmented) {
    return (
      <div className="viz-segmented" role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <span className="viz-select">
      <select aria-label={label} value={value} onChange={(e) => onChange(e.currentTarget.value as V)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="viz-select-chevron" aria-hidden="true">
        <ConsoleGlyph name="chevron" size={20} />
      </span>
    </span>
  );
}

export interface ViewStepperProps {
  /** Accessible name of the group, e.g. "Grafik di kartu ini". */
  label: string;
  /** The title of every view, in order. */
  titles: string[];
  index: number;
  onChange: (index: number) => void;
}

/**
 * Steps through the views of a card that holds more than one chart: two
 * 44px buttons around "2 dari 3". Each button names the chart it opens, so
 * the step is never a guess; the count is announced politely.
 */
export function ViewStepper({ label, titles, index, onChange }: ViewStepperProps) {
  const n = titles.length;
  const prev = (index - 1 + n) % n;
  const next = (index + 1) % n;
  return (
    <div className="viz-stepper" role="group" aria-label={label}>
      <button type="button" className="viz-stepper-btn" aria-label={`Grafik sebelumnya: ${titles[prev]}`} title={titles[prev]} onClick={() => onChange(prev)}>
        <ConsoleGlyph name="prev" size={20} />
      </button>
      <span className="viz-stepper-count" aria-live="polite">
        {index + 1} dari {n}
      </span>
      <button type="button" className="viz-stepper-btn" aria-label={`Grafik berikutnya: ${titles[next]}`} title={titles[next]} onClick={() => onChange(next)}>
        <ConsoleGlyph name="next" size={20} />
      </button>
    </div>
  );
}

interface HoverState {
  index: number;
  x: number;
  y: number;
  width: number;
}

/**
 * Hover and focus readout for HTML charts. `bind(i)` goes on each mark (a
 * row, a column, a tile); `layer(render)` draws the one VizTip near the
 * pointer, kept inside the plot, as an overlay layer that takes no pointer.
 */
export function useHoverTip() {
  const plot = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const place = useCallback((index: number, clientX: number, clientY: number) => {
    const box = plot.current?.getBoundingClientRect();
    if (!box) return setHover({ index, x: 0, y: 0, width: 0 });
    setHover({ index, x: clientX - box.left, y: clientY - box.top, width: box.width });
  }, []);
  const bind = (index: number) => ({
    tabIndex: 0,
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => place(index, e.clientX, e.clientY),
    onPointerLeave: () => setHover(null),
    onFocus: (e: { currentTarget: HTMLElement }) => {
      const r = e.currentTarget.getBoundingClientRect();
      place(index, r.left + r.width / 2, r.top);
    },
    onBlur: () => setHover(null),
    "data-active": hover?.index === index ? "" : undefined,
  });
  const layer = (render: (index: number) => ReactNode) => {
    if (!hover) return null;
    const flip = hover.width > 0 && hover.x > hover.width / 2;
    return (
      <div className="viz-tip-layer" data-flip={flip ? "" : undefined} style={{ insetBlockStart: hover.y, insetInlineStart: hover.x }} aria-hidden="true">
        {render(hover.index)}
      </div>
    );
  };
  return { plot, bind, layer, active: hover?.index ?? null };
}
